import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Building, BuildingType } from '@/config/buildingConfig'
import { getFacilityDef, getFacilityLevel, getMaxAllowedLevel, getFacilityMaxLevel, getFacilitySpec } from '@/config/facilityRegistry'
import { getDivisionOrNone, courtLicenseGate, meetsDivision } from '@/config/federationConfig'
import { Staff, StaffType, STAFF_DEFINITIONS, createStaff, deriveSkills, assignAvatarId } from '@/config/staffConfig'
import { getNegotiationEffects } from '@/game/systems/staffEffects'
import type { Tournament, TournamentType } from '@/config/tournamentConfig'
import { CLUB_LEVELS, isFeatureUnlocked } from '@/config/clubLevelConfig'
import type { EventType, GameEffect, PendingChain } from '@/config/eventsConfig'
import {
  Player, PlayerSnapshot, PLAYER_HISTORY_LIMIT, ROSTER_RULES, getRosterCap, generateStats,
} from '@/config/playersConfig'
import { createRng, hashString } from '@/game/utils/rng'
import type { LeagueState, LeagueHistoryEntry } from '@/config/leagueConfig'
import type { CircuitState } from '@/config/circuitConfig'
import type { Difficulty } from '@/config/difficultyConfig'
import type { RegionId } from '@/config/regionConfig'
import type { GameMode } from '@/config/gameModesConfig'
import type { StoryBeat } from '@/config/storyConfig'
import type { NewsItem } from '@/hooks/useClubNews'
import type { SeasonObjective, ClubOffer } from '@/config/careerConfig'
import { BOARD_CONFIG } from '@/config/careerConfig'
import { DAILY_ACTIONS } from '@/config/campusConfig'
import type { ConstructionJob, ConstructionInput, CompletedWork } from '@/config/constructionConfig'
import { RECENT_COMPLETIONS_LIMIT } from '@/config/constructionConfig'

export interface Court {
  id: string
  x: number
  y: number
  type: 'outdoor' | 'indoor'
  condition: number // 0-100
  pricePerHour: number
  isOccupied: boolean
  /** False while an upgrade's works are in progress (court closed, no income).
   *  Undefined on legacy courts → treated as operational. */
  isOperational?: boolean
}

export interface Transaction {
  id: string
  day: number
  amount: number
  type: 'income' | 'expense'
  description: string
  /** True for capital outflows (building courts/structures). These are cash
   *  out (counted in cash flow) but NOT operating expenses: the P&L spreads
   *  their cost over the asset's life as depreciation instead. */
  capex?: boolean
}

export interface ActiveLoan {
  loanId: string
  remainingMonths: number
  totalPaid: number
}

export interface ActiveSponsorship {
  sponsorshipId: string
  startMonth: number
  endMonth: number
}

export interface GameState {
  // Game status
  isPlaying: boolean
  isPaused: boolean
  gameSpeed: number // 1, 2, or 4
  
  // Time
  day: number
  week: number
  month: number
  
  // Economy
  money: number
  totalIncome: number
  totalExpenses: number
  transactions: Transaction[]
  
  // Courts
  courts: Court[]
  
  // Buildings
  buildings: Building[]
  
  // Staff
  staff: Staff[]
  
  // Reputation
  reputation: number // 0-5 stars
  totalCustomers: number
  satisfiedCustomers: number

  // Memberships
  enabledMembershipTiers: string[]
  memberCounts: Record<string, number>

  // Loans
  activeLoans: ActiveLoan[]

  // Sponsorships
  activeSponsorships: ActiveSponsorship[]

  // Tournaments
  activeTournament: Tournament | null
  tournamentOffers: TournamentType[]
  tournamentsCompleted: number

  // Court upgrades
  courtUpgrades: Record<string, string[]>

  // Academy
  activeCourses: string[]
  courseEnrollments: Record<string, number>

  // Goals
  claimedGoals: string[]

  // Weather
  currentWeather: string

  // Services
  enabledServices: string[]
  servicePrices: Record<string, number>

  // Club level progression
  clubLevel: number
  pendingLevelUp: number | null // level to show in modal

  // Persistent effects & event chains
  activeEffects: GameEffect[]
  pendingEventChains: PendingChain[]
  pendingEvent: EventType | null // event awaiting player decision

  // Player roster
  players: Player[]
  transferMarket: Player[]
  /** Weekly {day, level, form} snapshots per rostered player (ficha/report) */
  playerHistory: Record<string, PlayerSnapshot[]>

  // League
  league: LeagueState | null
  leagueHistory: LeagueHistoryEntry[]

  // Circuito FIP (competing branch; follows the league season)
  circuit: CircuitState | null

  // Estrellas del Club (prestige milestones, permanent)
  clubStars: string[]

  // Facility specializations: buildingId -> chosen spec id (permanent)
  buildingSpecs: Record<string, string>

  // Win / lose state
  daysInDebt: number // consecutive days closed with negative money
  gameResult: 'won' | 'lost' | null
  gameOverReason: 'bankruptcy' | 'fired' | null
  resultAcknowledged: boolean // victory screen dismissed (sandbox mode)

  // Difficulty (set at new game; 'normal' is the identity profile)
  difficulty: Difficulty

  // Starting region (set at new game; 'costa_sol' is the identity profile)
  region: RegionId

  // Narrative (StorySystem): beats already fired (persisted, de-dup), the modal
  // beat queue and the social-feed story posts (both transient — never saved)
  seenStoryBeats: string[]
  pendingStoryBeats: StoryBeat[]
  storyNews: NewsItem[]

  // Game mode / fantasy (only 'director' playable today)
  gameMode: GameMode

  // Board (career): confidence in the manager + the season's objective
  boardConfidence: number
  seasonObjective: SeasonObjective | null
  managerClubName: string

  // Campus daily actions: action id -> last day it was run
  dailyActionLog: Record<string, number>

  // Construction: works in progress (paid up front, materialise after N days)
  constructionQueue: ConstructionJob[]
  // Finished works awaiting their "construcción terminada" reveal (transient)
  pendingConstructionReveals: ConstructionJob[]
  // Last few finished works, newest first (feeds the campus postcards strip)
  recentCompletions: CompletedWork[]

  // Actions
  setPlaying: (playing: boolean) => void
  setPaused: (paused: boolean) => void
  setGameSpeed: (speed: number) => void
  advanceDay: () => void
  addMoney: (amount: number, description: string) => void
  subtractMoney: (amount: number, description: string, opts?: { capex?: boolean }) => void
  addCourt: (court: Court) => void
  updateCourt: (id: string, updates: Partial<Court>) => void
  addBuilding: (building: Building) => void
  updateBuilding: (id: string, updates: Partial<Building>) => void
  /** Raise a building one level if affordable and below maxLevel; pays the cost */
  upgradeBuilding: (id: string) => boolean

  // Construction queue
  /** Pay for and enqueue a timed construction job; false if unaffordable/conflicting */
  queueConstruction: (input: ConstructionInput) => boolean
  /** Materialise a finished job (called by ConstructionSystem); returns it for events */
  completeConstruction: (jobId: string) => ConstructionJob | null
  /** Dismiss the front "works finished" reveal */
  dismissConstructionReveal: () => void
  addStaff: (staff: Staff) => void
  removeStaff: (id: string) => void
  updateStaff: (id: string, updates: Partial<Staff>) => void
  /** Hire a role through the standard flow (requirements, cap, cost) */
  hireStaff: (type: StaffType) => boolean
  hasBuilding: (type: BuildingType) => boolean
  hasStaff: (type: StaffType) => boolean
  updateReputation: (satisfied: boolean) => void
  addReputation: (amount: number) => void

  // Membership actions
  toggleMembershipTier: (tierId: string) => void
  setMemberCounts: (counts: Record<string, number>) => void

  // Loan actions
  addActiveLoan: (loan: ActiveLoan) => void
  removeActiveLoan: (loanId: string) => void
  setActiveLoans: (loans: ActiveLoan[]) => void

  // Sponsorship actions
  addActiveSponsorship: (sponsorship: ActiveSponsorship) => void
  removeActiveSponsorship: (sponsorshipId: string) => void
  setActiveSponsorships: (sponsorships: ActiveSponsorship[]) => void

  // Tournament actions
  setActiveTournament: (tournament: Tournament | null) => void
  setTournamentOffers: (offers: TournamentType[]) => void
  incrementTournamentsCompleted: () => void

  // Court upgrade actions
  applyCourtUpgrade: (courtId: string, upgradeId: string) => void
  getCourtUpgrades: (courtId: string) => string[]

  // Academy actions
  toggleCourse: (courseId: string) => void
  setCourseEnrollments: (enrollments: Record<string, number>) => void

  // Goals actions
  claimGoal: (goalId: string) => void
  isGoalClaimed: (goalId: string) => boolean

  // Weather actions
  setWeather: (weather: string) => void

  // Services actions
  toggleService: (serviceId: string) => void
  setServicePrice: (serviceId: string, price: number) => void

  // Difficulty
  setDifficulty: (difficulty: Difficulty) => void

  // Region
  setRegion: (region: RegionId) => void

  // Narrative (StorySystem)
  enqueueStoryBeat: (beat: StoryBeat) => void
  dismissStoryBeat: () => void
  markStoryBeatSeen: (id: string) => void
  pushStoryNews: (item: NewsItem) => void

  // Game mode
  setGameMode: (mode: GameMode) => void

  // Board (career)
  setBoardConfidence: (value: number) => void
  setSeasonObjective: (objective: SeasonObjective | null) => void
  setManagerClubName: (name: string) => void
  /** Selective reset to take over a new club, preserving difficulty + career */
  startNewClubFromOffer: (offer: ClubOffer) => void

  // Club level actions
  setClubLevel: (level: number) => void
  setPendingLevelUp: (level: number | null) => void
  checkLevelUp: () => void

  // Effects & chains actions
  addEffect: (effect: GameEffect) => void
  removeEffect: (effectId: string) => void
  cleanExpiredEffects: () => void
  addPendingChain: (chain: PendingChain) => void
  removePendingChain: (eventId: string) => void
  setPendingEvent: (eventType: EventType | null) => void

  // Player roster actions
  signPlayer: (player: Player) => boolean
  /** Haggle a market candidate's signing fee (once per candidate).
   *  Success (-15%) or failure (+5%, they get annoyed). */
  negotiateSigningFee: (playerId: string) => 'accepted' | 'rejected' | 'unavailable'
  releasePlayer: (playerId: string) => void
  updatePlayer: (playerId: string, updates: Partial<Player>) => void
  setPlayers: (players: Player[]) => void
  setTransferMarket: (players: Player[]) => void
  /** Append a weekly snapshot per rostered player (ring buffer) */
  recordPlayerSnapshots: () => void

  // League actions
  setLeague: (league: LeagueState | null) => void
  addLeagueHistory: (entry: LeagueHistoryEntry) => void

  // Circuit actions
  setCircuit: (circuit: CircuitState | null) => void

  // Prestige actions
  awardClubStar: (id: string) => void

  /** Pick a facility's terminal branch (needs max level; permanent) */
  chooseSpecialization: (buildingId: string, specId: string) => boolean

  // Win / lose actions
  setDaysInDebt: (days: number) => void
  setGameResult: (result: 'won' | 'lost' | null) => void
  setGameOverReason: (reason: 'bankruptcy' | 'fired' | null) => void
  setResultAcknowledged: (acknowledged: boolean) => void

  // Campus daily actions
  canRunDailyAction: (actionId: string) => boolean
  runDailyAction: (actionId: string) => boolean

  resetGame: () => void
}

const initialState = {
  isPlaying: false,
  isPaused: false,
  gameSpeed: 1,
  day: 1,
  week: 1,
  month: 1,
  money: 10000, // Starting capital
  totalIncome: 0,
  totalExpenses: 0,
  transactions: [] as Transaction[],
  courts: [] as Court[],
  buildings: [] as Building[],
  staff: [] as Staff[],
  reputation: 3, // Start at 3 stars
  totalCustomers: 0,
  satisfiedCustomers: 0,
  enabledMembershipTiers: [] as string[],
  memberCounts: {} as Record<string, number>,
  activeLoans: [] as ActiveLoan[],
  activeSponsorships: [] as ActiveSponsorship[],
  activeTournament: null as Tournament | null,
  tournamentOffers: [] as TournamentType[],
  tournamentsCompleted: 0,
  courtUpgrades: {} as Record<string, string[]>,
  activeCourses: [] as string[],
  courseEnrollments: {} as Record<string, number>,
  claimedGoals: [] as string[],
  currentWeather: 'sunny' as string,
  enabledServices: ['court_rental'] as string[], // court rental enabled by default
  servicePrices: {} as Record<string, number>,
  clubLevel: 0,
  pendingLevelUp: null as number | null,
  activeEffects: [] as GameEffect[],
  pendingEventChains: [] as PendingChain[],
  pendingEvent: null as EventType | null,
  players: [] as Player[],
  transferMarket: [] as Player[],
  playerHistory: {} as Record<string, PlayerSnapshot[]>,
  league: null as LeagueState | null,
  leagueHistory: [] as LeagueHistoryEntry[],
  circuit: null as CircuitState | null,
  clubStars: [] as string[],
  buildingSpecs: {} as Record<string, string>,
  daysInDebt: 0,
  gameResult: null as 'won' | 'lost' | null,
  gameOverReason: null as 'bankruptcy' | 'fired' | null,
  resultAcknowledged: false,
  difficulty: 'normal' as Difficulty,
  region: 'costa_sol' as RegionId,
  seenStoryBeats: [] as string[],
  pendingStoryBeats: [] as StoryBeat[],
  storyNews: [] as NewsItem[],
  gameMode: 'director' as GameMode,
  boardConfidence: BOARD_CONFIG.startingConfidence,
  seasonObjective: null as SeasonObjective | null,
  managerClubName: '',
  dailyActionLog: {} as Record<string, number>,
  constructionQueue: [] as ConstructionJob[],
  pendingConstructionReveals: [] as ConstructionJob[],
  recentCompletions: [] as CompletedWork[],
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setPlaying: (playing) => set({ isPlaying: playing }),
      
      setPaused: (paused) => set({ isPaused: paused }),
      
      setGameSpeed: (speed) => set({ gameSpeed: speed }),
      
      advanceDay: () => {
        const { day } = get()
        const newDay = day + 1
        const newWeek = Math.floor((newDay - 1) / 7) + 1
        const newMonth = Math.floor((newDay - 1) / 28) + 1
        set({ day: newDay, week: newWeek, month: newMonth })
      },
      
      addMoney: (amount, description) => {
        const { money, totalIncome, transactions, day } = get()
        const transaction: Transaction = {
          id: crypto.randomUUID(),
          day,
          amount,
          type: 'income',
          description,
        }
        set({
          money: money + amount,
          totalIncome: totalIncome + amount,
          transactions: [...transactions.slice(-50), transaction],
        })
      },
      
      subtractMoney: (amount, description, opts) => {
        const { money, totalExpenses, transactions, day } = get()
        const transaction: Transaction = {
          id: crypto.randomUUID(),
          day,
          amount,
          type: 'expense',
          description,
          ...(opts?.capex ? { capex: true } : {}),
        }
        set({
          money: money - amount,
          totalExpenses: totalExpenses + amount,
          transactions: [...transactions.slice(-50), transaction],
        })
      },
      
      addCourt: (court) => {
        const { courts } = get()
        set({ courts: [...courts, court] })
      },
      
      updateCourt: (id, updates) => {
        const { courts } = get()
        set({
          courts: courts.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        })
      },
      
      addBuilding: (building) => {
        const { buildings } = get()
        set({ buildings: [...buildings, building] })
      },
      
      updateBuilding: (id, updates) => {
        const { buildings } = get()
        set({
          buildings: buildings.map((b) => (b.id === id ? { ...b, ...updates } : b)),
        })
      },

      upgradeBuilding: (id) => {
        const { buildings, money, clubLevel, league } = get()
        const building = buildings.find((b) => b.id === id)
        if (!building) return false
        const def = getFacilityDef(building.type)
        if (!def) return false
        // Soft caps: club level + Licencia Federativa (league division)
        if (building.level >= getMaxAllowedLevel(building.type, clubLevel, getDivisionOrNone(league))) return false
        const next = getFacilityLevel(building.type, building.level + 1)
        if (!next) return false
        if (money < next.cost) return false
        get().subtractMoney(next.cost, `Mejora ${def.name} a nivel ${next.level}`)
        set({
          buildings: get().buildings.map((b) =>
            b.id === id ? { ...b, level: b.level + 1 } : b
          ),
        })
        return true
      },

      queueConstruction: (input) => {
        const { money, day, constructionQueue, courts, league } = get()
        if (input.cost > money) return false

        // Licencia Federativa: expanding the court footprint needs division
        // (counting queued works so multiple jobs can't slip past the gate)
        if (input.kind === 'court' && input.courtType) {
          const queued = constructionQueue.filter(j => j.kind === 'court')
          const total = courts.length + queued.length
          const indoor = courts.filter(c => c.type === 'indoor').length
            + queued.filter(j => j.courtType === 'indoor').length
          const gate = courtLicenseGate(total, indoor, input.courtType)
          if (gate !== null && !meetsDivision(getDivisionOrNone(league), gate)) return false
        }

        // Guard against double-queueing the same works
        if (input.kind === 'building' &&
            constructionQueue.some(j => j.kind === 'building' && j.buildingType === input.buildingType)) {
          return false
        }
        if (input.kind === 'building_upgrade' &&
            constructionQueue.some(j => j.kind === 'building_upgrade' && j.buildingId === input.buildingId)) {
          return false
        }
        // Only one upgrade in progress per court (it's closed during the works)
        if (input.kind === 'court_upgrade' &&
            constructionQueue.some(j => j.kind === 'court_upgrade' && j.courtId === input.courtId)) {
          return false
        }

        const { cost, ...jobData } = input
        const job: ConstructionJob = {
          ...jobData,
          id: crypto.randomUUID(),
          startDay: day,
          endDay: day + input.totalDays,
        }
        get().subtractMoney(cost, `Obras: ${input.label}`, { capex: true })
        set({ constructionQueue: [...get().constructionQueue, job] })
        return true
      },

      completeConstruction: (jobId) => {
        const job = get().constructionQueue.find(j => j.id === jobId)
        if (!job) return null

        // Always consume the job from the queue
        set({ constructionQueue: get().constructionQueue.filter(j => j.id !== jobId) })

        // Materialise the structure; `materialised` gates the reveal so a
        // no-op (e.g. building already exists, or a stale upgrade target) never
        // shows a "works finished" modal for nothing.
        let materialised = false
        if (job.kind === 'court' && job.courtType) {
          get().addCourt({
            id: `court-${crypto.randomUUID()}`,
            x: 0,
            y: 0,
            type: job.courtType,
            condition: 100,
            pricePerHour: job.pricePerHour ?? (job.courtType === 'outdoor' ? 15 : 25),
            isOccupied: false,
            isOperational: true,
          })
          materialised = true
        } else if (job.kind === 'court_upgrade' && job.courtId && job.courtUpgradeId) {
          // Works finished: apply the upgrade and reopen the court.
          const court = get().courts.find(c => c.id === job.courtId)
          if (court) {
            get().applyCourtUpgrade(job.courtId, job.courtUpgradeId)
            get().updateCourt(job.courtId, { isOperational: true })
            materialised = true
          }
        } else if (job.kind === 'building' && job.buildingType && !get().hasBuilding(job.buildingType)) {
          get().addBuilding({
            id: job.buildingId ?? crypto.randomUUID(),
            type: job.buildingType,
            x: 0,
            y: 0,
            level: 1,
            condition: 100,
          })
          materialised = true
        } else if (job.kind === 'building_upgrade' && job.buildingId && job.targetLevel !== undefined) {
          const building = get().buildings.find(b => b.id === job.buildingId)
          if (building && getFacilityDef(building.type)) {
            // Clamp against the SOFT-CAPPED max (not the track max) so a
            // queued job can never jump a club-level or license gate.
            const maxLevel = getMaxAllowedLevel(building.type, get().clubLevel, getDivisionOrNone(get().league))
            const level = Math.min(Math.max(maxLevel, building.level), job.targetLevel)
            if (level > building.level) {
              get().updateBuilding(job.buildingId, { level })
              materialised = true
            }
          }
        }

        if (materialised) {
          const completed: CompletedWork = {
            visualId: job.visualId,
            label: job.label,
            tier: job.tier,
            kind: job.kind,
            day: get().day,
          }
          set({
            pendingConstructionReveals: [...get().pendingConstructionReveals, job],
            recentCompletions: [completed, ...get().recentCompletions].slice(0, RECENT_COMPLETIONS_LIMIT),
          })
        }

        return materialised ? job : null
      },

      dismissConstructionReveal: () => {
        set({ pendingConstructionReveals: get().pendingConstructionReveals.slice(1) })
      },

      addStaff: (staff) => {
        const { staff: currentStaff } = get()
        set({ staff: [...currentStaff, staff] })
      },
      
      removeStaff: (id) => {
        const { staff } = get()
        set({ staff: staff.filter((s) => s.id !== id) })
      },
      
      updateStaff: (id, updates) => {
        const { staff } = get()
        set({
          staff: staff.map((s) => (s.id === id ? { ...s, ...updates } : s)),
        })
      },

      hireStaff: (type) => {
        const state = get()
        const def = STAFF_DEFINITIONS[type]
        if (!def) return false
        if (def.feature && !isFeatureUnlocked(def.feature, state.clubLevel)) return false
        if (def.requires && !state.hasBuilding(def.requires)) return false
        if (state.staff.filter(s => s.type === type).length >= def.maxCount) return false
        if (state.money < def.baseSalary) return false

        const member = createStaff(type, { day: state.day })
        state.addStaff(member)
        state.subtractMoney(def.baseSalary, `Contratación ${def.name}`)
        return true
      },


      hasBuilding: (type) => {
        return get().buildings.some((b) => b.type === type)
      },
      
      hasStaff: (type) => {
        return get().staff.some((s) => s.type === type)
      },
      
      updateReputation: (satisfied) => {
        const { totalCustomers, satisfiedCustomers } = get()
        const newTotal = totalCustomers + 1
        const newSatisfied = satisfiedCustomers + (satisfied ? 1 : 0)
        const newReputation = Math.min(5, Math.max(1, (newSatisfied / newTotal) * 5))
        set({
          totalCustomers: newTotal,
          satisfiedCustomers: newSatisfied,
          reputation: Math.round(newReputation * 10) / 10,
        })
      },
      
      addReputation: (amount) => {
        const { reputation } = get()
        set({ reputation: Math.min(5, Math.max(0, reputation + amount)) })
      },
      
      // Membership actions
      toggleMembershipTier: (tierId) => {
        const { enabledMembershipTiers } = get()
        const updated = enabledMembershipTiers.includes(tierId)
          ? enabledMembershipTiers.filter(id => id !== tierId)
          : [...enabledMembershipTiers, tierId]
        set({ enabledMembershipTiers: updated })
      },

      setMemberCounts: (counts) => {
        set({ memberCounts: counts })
      },

      // Loan actions
      addActiveLoan: (loan) => {
        set({ activeLoans: [...get().activeLoans, loan] })
      },

      removeActiveLoan: (loanId) => {
        set({ activeLoans: get().activeLoans.filter(l => l.loanId !== loanId) })
      },

      setActiveLoans: (loans) => {
        set({ activeLoans: loans })
      },

      // Sponsorship actions
      addActiveSponsorship: (sponsorship) => {
        set({ activeSponsorships: [...get().activeSponsorships, sponsorship] })
      },

      removeActiveSponsorship: (sponsorshipId) => {
        set({ activeSponsorships: get().activeSponsorships.filter(s => s.sponsorshipId !== sponsorshipId) })
      },

      setActiveSponsorships: (sponsorships) => {
        set({ activeSponsorships: sponsorships })
      },

      // Tournament actions
      setActiveTournament: (tournament) => {
        set({ activeTournament: tournament })
      },

      setTournamentOffers: (offers) => {
        set({ tournamentOffers: offers })
      },

      incrementTournamentsCompleted: () => {
        set({ tournamentsCompleted: get().tournamentsCompleted + 1 })
      },

      // Court upgrade actions
      applyCourtUpgrade: (courtId, upgradeId) => {
        const { courtUpgrades } = get()
        const existing = courtUpgrades[courtId] || []
        if (existing.includes(upgradeId)) return
        set({
          courtUpgrades: {
            ...courtUpgrades,
            [courtId]: [...existing, upgradeId],
          },
        })
      },

      getCourtUpgrades: (courtId) => {
        return get().courtUpgrades[courtId] || []
      },

      // Academy actions
      toggleCourse: (courseId) => {
        const { activeCourses, courseEnrollments } = get()
        if (activeCourses.includes(courseId)) {
          const { [courseId]: _, ...rest } = courseEnrollments
          set({
            activeCourses: activeCourses.filter(id => id !== courseId),
            courseEnrollments: rest,
          })
        } else {
          set({ activeCourses: [...activeCourses, courseId] })
        }
      },

      setCourseEnrollments: (enrollments) => {
        set({ courseEnrollments: enrollments })
      },

      // Goals actions
      claimGoal: (goalId) => {
        const { claimedGoals } = get()
        if (!claimedGoals.includes(goalId)) {
          set({ claimedGoals: [...claimedGoals, goalId] })
        }
      },

      isGoalClaimed: (goalId) => {
        return get().claimedGoals.includes(goalId)
      },

      // Weather actions
      setWeather: (weather) => {
        set({ currentWeather: weather })
      },

      // Services actions
      toggleService: (serviceId) => {
        const { enabledServices } = get()
        const updated = enabledServices.includes(serviceId)
          ? enabledServices.filter(id => id !== serviceId)
          : [...enabledServices, serviceId]
        set({ enabledServices: updated })
      },

      setServicePrice: (serviceId, price) => {
        set({ servicePrices: { ...get().servicePrices, [serviceId]: price } })
      },

      // Difficulty
      setDifficulty: (difficulty) => set({ difficulty }),

      // Region
      setRegion: (region) => set({ region }),

      // Narrative (StorySystem): modal queue + story feed + seen de-dup
      enqueueStoryBeat: (beat) => set(s => ({ pendingStoryBeats: [...s.pendingStoryBeats, beat] })),
      dismissStoryBeat: () => set(s => ({ pendingStoryBeats: s.pendingStoryBeats.slice(1) })),
      markStoryBeatSeen: (id) => set(s => (s.seenStoryBeats.includes(id) ? s : { seenStoryBeats: [...s.seenStoryBeats, id] })),
      pushStoryNews: (item) => set(s => ({ storyNews: [item, ...s.storyNews.filter(n => n.id !== item.id)].slice(0, 6) })),

      // Game mode
      setGameMode: (mode) => set({ gameMode: mode }),

      // Board (career)
      setBoardConfidence: (value) => set({ boardConfidence: Math.max(0, Math.min(BOARD_CONFIG.maxConfidence, value)) }),
      setSeasonObjective: (objective) => set({ seasonObjective: objective }),
      setManagerClubName: (name) => set({ managerClubName: name }),

      startNewClubFromOffer: (offer) => {
        const { difficulty, region } = get()
        // Reset everything club-related but keep the manager's difficulty and
        // keep the game running (initialState.isPlaying is false). The career
        // meta-layer lives in careerStore and is untouched here. Roster, courts
        // and the league for the offered division are seeded by careerActions.
        set({
          ...initialState,
          isPlaying: true,
          isPaused: false,
          difficulty,
          region,
          money: offer.startingMoney,
          clubLevel: offer.startingClubLevel,
          managerClubName: offer.clubName,
          boardConfidence: BOARD_CONFIG.startingConfidence,
          seasonObjective: null,
        })
      },

      // Club level actions
      setClubLevel: (level) => set({ clubLevel: level }),

      setPendingLevelUp: (level) => set({ pendingLevelUp: level }),

      checkLevelUp: () => {
        const state = get()
        // In Coach mode you don't build the club, so its level is fixed at the
        // prestige you were handed — no build-progress level-ups or windfalls.
        if (state.gameMode === 'coach') return
        const nextLevel = CLUB_LEVELS.find(l => l.level === state.clubLevel + 1)
        if (!nextLevel) return

        const allMet = nextLevel.requirements.every(req => {
          switch (req.type) {
            case 'courts': return state.courts.length >= req.value
            case 'buildings': return state.buildings.length >= req.value
            case 'staff': return state.staff.length >= req.value
            case 'reputation': return state.reputation >= req.value
            case 'services': return state.enabledServices.length >= req.value
            case 'upgrades': {
              const totalUpgrades = Object.values(state.courtUpgrades).reduce((sum, u) => sum + u.length, 0)
              return totalUpgrades >= req.value
            }
            case 'members': {
              const totalMembers = Object.values(state.memberCounts).reduce((sum, c) => sum + c, 0)
              return totalMembers >= req.value
            }
            case 'league_division':
              return state.league !== null && state.league.division <= req.value
            case 'facility_level': {
              const qualifying = state.buildings.filter(b => (b.level ?? 1) >= req.value).length
              return qualifying >= (req.count ?? 1)
            }
            default: return false
          }
        })

        if (allMet) {
          set({
            clubLevel: nextLevel.level,
            pendingLevelUp: nextLevel.level,
          })
          // Apply rewards
          if (nextLevel.reward.money > 0) {
            state.addMoney(nextLevel.reward.money, `Nivel ${nextLevel.level}: ${nextLevel.name}`)
          }
          if (nextLevel.reward.reputation > 0) {
            state.addReputation(nextLevel.reward.reputation)
          }
        }
      },

      // Effects & chains actions
      addEffect: (effect) => {
        set({ activeEffects: [...get().activeEffects, effect] })
      },

      removeEffect: (effectId) => {
        set({ activeEffects: get().activeEffects.filter(e => e.id !== effectId) })
      },

      cleanExpiredEffects: () => {
        const { activeEffects, day } = get()
        const remaining = activeEffects.filter(e => e.expiresOnDay === 0 || e.expiresOnDay > day)
        if (remaining.length !== activeEffects.length) {
          set({ activeEffects: remaining })
        }
      },

      addPendingChain: (chain) => {
        set({ pendingEventChains: [...get().pendingEventChains, chain] })
      },

      removePendingChain: (eventId) => {
        set({ pendingEventChains: get().pendingEventChains.filter(c => c.eventId !== eventId) })
      },

      setPendingEvent: (eventType) => {
        set({ pendingEvent: eventType })
      },

      // Player roster actions
      signPlayer: (player) => {
        const state = get()
        if (state.players.length >= getRosterCap(state.clubLevel)) return false
        if (state.money < player.signingFee) return false

        state.subtractMoney(player.signingFee, `Fichaje: ${player.name}`)
        set({
          players: [...get().players, { ...player, joinedDay: state.day }],
          transferMarket: get().transferMarket.filter(p => p.id !== player.id),
        })
        return true
      },

      negotiateSigningFee: (playerId) => {
        const state = get()
        const candidate = state.transferMarket.find(p => p.id === playerId)
        if (!candidate || candidate.negotiated) return 'unavailable'

        const { successChance } = getNegotiationEffects(state.staff)
        const accepted = Math.random() < successChance
        const signingFee = Math.round(candidate.signingFee * (accepted ? 0.85 : 1.05))

        set({
          transferMarket: state.transferMarket.map(p =>
            p.id === playerId ? { ...p, signingFee, negotiated: true } : p,
          ),
        })
        return accepted ? 'accepted' : 'rejected'
      },

      releasePlayer: (playerId) => {
        const state = get()
        const player = state.players.find(p => p.id === playerId)
        if (!player) return

        const severance = player.salary * ROSTER_RULES.severanceWeeks
        state.subtractMoney(severance, `Finiquito: ${player.name}`)
        const { [playerId]: _removed, ...restHistory } = get().playerHistory
        set({
          players: get().players.filter(p => p.id !== playerId),
          playerHistory: restHistory,
        })
      },

      updatePlayer: (playerId, updates) => {
        set({
          players: get().players.map(p => p.id === playerId ? { ...p, ...updates } : p),
        })
      },

      setPlayers: (players) => set({ players }),

      setTransferMarket: (players) => set({ transferMarket: players }),

      recordPlayerSnapshots: () => {
        const { players, playerHistory, day } = get()
        if (players.length === 0) return
        const updated = { ...playerHistory }
        for (const p of players) {
          const snaps = updated[p.id] ?? []
          updated[p.id] = [...snaps, { day, level: p.level, form: p.form }]
            .slice(-PLAYER_HISTORY_LIMIT)
        }
        set({ playerHistory: updated })
      },

      // League actions
      setLeague: (league) => set({ league }),

      addLeagueHistory: (entry) => {
        set({ leagueHistory: [...get().leagueHistory, entry] })
      },

      // Circuit actions
      setCircuit: (circuit) => set({ circuit }),

      // Prestige actions
      awardClubStar: (id) => {
        const { clubStars } = get()
        if (!clubStars.includes(id)) set({ clubStars: [...clubStars, id] })
      },

      chooseSpecialization: (buildingId, specId) => {
        const { buildings, buildingSpecs } = get()
        if (buildingSpecs[buildingId]) return false // permanent, no re-spec
        const building = buildings.find(b => b.id === buildingId)
        if (!building) return false
        if (building.level < getFacilityMaxLevel(building.type)) return false
        if (!getFacilitySpec(building.type, specId)) return false
        set({ buildingSpecs: { ...buildingSpecs, [buildingId]: specId } })
        return true
      },

      // Win / lose actions
      setDaysInDebt: (days) => set({ daysInDebt: days }),

      setGameResult: (result) => set({ gameResult: result }),

      setGameOverReason: (reason) => set({ gameOverReason: reason }),

      setResultAcknowledged: (acknowledged) => set({ resultAcknowledged: acknowledged }),

      // Campus daily actions
      canRunDailyAction: (actionId) => {
        const state = get()
        const def = DAILY_ACTIONS.find(a => a.id === actionId)
        if (!def) return false
        const lastRun = state.dailyActionLog[actionId]
        if (lastRun !== undefined && state.day < lastRun + def.cooldownDays) return false
        if (state.money < def.cost) return false

        switch (actionId) {
          case 'court_tuneup':
            return state.courts.some(c => c.condition < 100)
          case 'cafe_promo':
            return state.hasBuilding('cafe')
          case 'member_newsletter':
            return state.hasBuilding('reception') && state.hasStaff('receptionist')
          default:
            return true
        }
      },

      runDailyAction: (actionId) => {
        const state = get()
        if (!state.canRunDailyAction(actionId)) return false
        const def = DAILY_ACTIONS.find(a => a.id === actionId)!

        if (def.cost > 0) {
          state.subtractMoney(def.cost, `Campus: ${def.name}`)
        }

        switch (actionId) {
          case 'court_tuneup': {
            const worst = [...state.courts].sort((a, b) => a.condition - b.condition)[0]
            if (worst) {
              state.updateCourt(worst.id, { condition: Math.min(100, worst.condition + 10) })
            }
            break
          }
          case 'cafe_promo': {
            state.addEffect({
              id: `cafe-promo-${state.day}`,
              source: 'Promo del dia',
              type: 'income_modifier',
              value: 1.1,
              expiresOnDay: state.day + 1,
              description: '+10% ingresos por la promo de cafeteria',
            })
            break
          }
          case 'member_newsletter': {
            state.addReputation(0.02)
            break
          }
        }

        set({ dailyActionLog: { ...get().dailyActionLog, [actionId]: state.day } })
        return true
      },

      resetGame: () => set(initialState),
    }),
    {
      name: 'padel-manager-save',
      version: 13,
      // v0 -> v1: environmentTheme (Phaser-era visual theme) removed
      // v1 -> v2: difficulty added (defaults to 'normal' for old saves)
      // v2 -> v3: board/career fields added (confidence/objective/club name)
      // v3 -> v4: gameOverReason added
      // v4 -> v5: gameMode added
      // v5 -> v6: constructionQueue added (timed construction)
      // v6 -> v7: region added (starting region; 'costa_sol' is identity)
      // v7 -> v8: narrative added (seenStoryBeats; story queue/feed are transient)
      // v8 -> v9: dead parcel/terrain subsystem removed; player multi-stats +
      //           injury/fatigue; staff sub-skills + avatar; playerHistory
      // v9 -> v10: circuit (Circuito FIP calendar/ranking) added
      // v10 -> v11: clubStars (Estrellas del Club prestige milestones)
      // v11 -> v12: buildingSpecs (facility terminal specializations)
      // v12 -> v13: recentCompletions (campus postcards strip)
      migrate: (persisted: unknown) => {
        if (persisted && typeof persisted === 'object') {
          const p = persisted as Record<string, unknown>
          delete p.environmentTheme
          delete p.pendingIntro // superseded by the StorySystem (gameStart beat)
          if (p.difficulty === undefined) p.difficulty = 'normal'
          if (p.boardConfidence === undefined) p.boardConfidence = 60
          if (p.seasonObjective === undefined) p.seasonObjective = null
          if (p.managerClubName === undefined) p.managerClubName = ''
          if (p.gameOverReason === undefined) p.gameOverReason = null
          if (p.gameMode === undefined) p.gameMode = 'director'
          if (p.constructionQueue === undefined) p.constructionQueue = []
          if (p.region === undefined) p.region = 'costa_sol'
          if (p.seenStoryBeats === undefined) p.seenStoryBeats = []

          // v9 — the isometric-era parcel/terrain subsystem is gone
          delete p.parcels
          delete p.selectedParcelId
          delete p.terrain

          // v9 — players (roster AND market candidates) gain stats/injury/
          // fatigue. Seeded per player id so re-running is idempotent and the
          // stats mean equals the stored level exactly.
          const migratePlayers = (list: unknown) => {
            if (!Array.isArray(list)) return
            for (const pl of list as Player[]) {
              if (!pl.stats) {
                pl.stats = generateStats(pl.level, createRng(hashString(pl.id)))
              }
              if (pl.injury === undefined) pl.injury = null
              if (pl.fatigue === undefined) pl.fatigue = 0
            }
          }
          migratePlayers(p.players)
          migratePlayers(p.transferMarket)

          // v9 — staff gain sub-skills (jitter around the legacy skill, so
          // the mean-driven formulas keep their numbers) and a stable avatar
          if (Array.isArray(p.staff)) {
            for (const s of p.staff as Staff[]) {
              if (!s.skills) s.skills = deriveSkills(s.type, s.skill, createRng(hashString(s.id)))
              if (!s.avatarId) s.avatarId = assignAvatarId(s.type, s.id)
            }
          }

          if (p.playerHistory === undefined) p.playerHistory = {}

          // v10 — Circuito FIP (the system seeds the calendar on the next week)
          if (p.circuit === undefined) p.circuit = null

          // v11 — Estrellas del Club
          if (p.clubStars === undefined) p.clubStars = []

          // v12 — facility specializations
          if (p.buildingSpecs === undefined) p.buildingSpecs = {}

          // v13 — campus postcards strip
          if (p.recentCompletions === undefined) p.recentCompletions = []
        }
        return persisted
      },
      partialize: (state) => ({
        day: state.day,
        week: state.week,
        month: state.month,
        money: state.money,
        totalIncome: state.totalIncome,
        totalExpenses: state.totalExpenses,
        transactions: state.transactions,
        courts: state.courts,
        buildings: state.buildings,
        staff: state.staff,
        reputation: state.reputation,
        totalCustomers: state.totalCustomers,
        satisfiedCustomers: state.satisfiedCustomers,
        enabledMembershipTiers: state.enabledMembershipTiers,
        memberCounts: state.memberCounts,
        activeLoans: state.activeLoans,
        activeSponsorships: state.activeSponsorships,
        activeTournament: state.activeTournament,
        tournamentOffers: state.tournamentOffers,
        tournamentsCompleted: state.tournamentsCompleted,
        courtUpgrades: state.courtUpgrades,
        activeCourses: state.activeCourses,
        courseEnrollments: state.courseEnrollments,
        claimedGoals: state.claimedGoals,
        currentWeather: state.currentWeather,
        enabledServices: state.enabledServices,
        servicePrices: state.servicePrices,
        clubLevel: state.clubLevel,
        difficulty: state.difficulty,
        region: state.region,
        seenStoryBeats: state.seenStoryBeats,
        gameMode: state.gameMode,
        boardConfidence: state.boardConfidence,
        seasonObjective: state.seasonObjective,
        managerClubName: state.managerClubName,
        activeEffects: state.activeEffects,
        pendingEventChains: state.pendingEventChains,
        pendingEvent: state.pendingEvent,
        players: state.players,
        transferMarket: state.transferMarket,
        playerHistory: state.playerHistory,
        league: state.league,
        leagueHistory: state.leagueHistory,
        circuit: state.circuit,
        clubStars: state.clubStars,
        buildingSpecs: state.buildingSpecs,
        daysInDebt: state.daysInDebt,
        gameResult: state.gameResult,
        gameOverReason: state.gameOverReason,
        resultAcknowledged: state.resultAcknowledged,
        dailyActionLog: state.dailyActionLog,
        constructionQueue: state.constructionQueue,
        recentCompletions: state.recentCompletions,
      }),
    }
  )
)

